import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import * as d3 from "d3";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getHierarchyData, HierarchyNode } from "../../api/trees";
import LoadingSpinner from "../Common/LoadingSpinner";

// Node dimensions
const NODE_WIDTH = 160;
const NODE_HEIGHT = 80;
const HORIZONTAL_SPACING = 40;
const VERTICAL_SPACING = 100;

// Generation labels
const GENERATION_LABELS: Record<number, string> = {
  0: "Selected Person",
  1: "Parents",
  2: "Grandparents",
  3: "Great-Grandparents",
  4: "2x Great-Grandparents",
  5: "3x Great-Grandparents",
  6: "4x Great-Grandparents",
};

interface AncestryTreeViewProps {
  treeId: number;
  rootPersonId?: number;
  onPersonSelect?: (personId: number) => void;
}

// Extended node type for D3 hierarchy
interface TreeNodeDatum extends HierarchyNode {
  _collapsed?: boolean;
}

type D3TreeNode = d3.HierarchyPointNode<TreeNodeDatum>;

/**
 * Ancestry-style family tree visualization using D3.js
 */
export default function AncestryTreeView({
  treeId,
  rootPersonId,
  onPersonSelect,
}: AncestryTreeViewProps) {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [collapsedNodes, setCollapsedNodes] = useState<Set<number>>(new Set());

  // Fetch hierarchy data
  const {
    data: hierarchyData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["hierarchy", treeId, rootPersonId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getHierarchyData(treeId, token, {
        rootId: rootPersonId,
        direction: "ancestors",
        maxGenerations: 10,
      });
    },
  });

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(width, 400), height: Math.max(height, 400) });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  // Convert our hierarchy to D3-compatible format
  const convertToD3Hierarchy = useCallback(
    (node: HierarchyNode): TreeNodeDatum => {
      const children: TreeNodeDatum[] = [];

      // For ancestors view, father and mother become children in D3 tree
      if (node.father && !collapsedNodes.has(node.id)) {
        children.push(convertToD3Hierarchy(node.father));
      }
      if (node.mother && !collapsedNodes.has(node.id)) {
        children.push(convertToD3Hierarchy(node.mother));
      }

      return {
        ...node,
        children: children.length > 0 ? children : undefined,
        _collapsed: collapsedNodes.has(node.id),
      };
    },
    [collapsedNodes]
  );

  // Toggle collapse state
  const toggleCollapse = useCallback((nodeId: number) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Handle node click
  const handleNodeClick = useCallback(
    (nodeId: number) => {
      if (onPersonSelect) {
        onPersonSelect(nodeId);
      } else {
        navigate(`/trees/${treeId}/persons/${nodeId}`);
      }
    },
    [navigate, treeId, onPersonSelect]
  );

  // Render the tree with D3
  useEffect(() => {
    if (!hierarchyData?.hierarchy || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const { width, height } = dimensions;
    const margin = { top: 40, right: 40, bottom: 40, left: 40 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create main group with zoom
    const g = svg
      .append("g")
      .attr("class", "tree-container")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 2])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Convert data and create hierarchy
    const d3Data = convertToD3Hierarchy(hierarchyData.hierarchy);
    const root = d3.hierarchy(d3Data, (d) => d.children);

    // Create tree layout - flip it so root is at bottom
    const treeLayout = d3
      .tree<TreeNodeDatum>()
      .nodeSize([NODE_WIDTH + HORIZONTAL_SPACING, NODE_HEIGHT + VERTICAL_SPACING]);

    const treeData = treeLayout(root);

    // Get all nodes and find bounds
    const nodes = treeData.descendants();
    const links = treeData.links();

    // Calculate bounds and center
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    nodes.forEach((n) => {
      minX = Math.min(minX, n.x);
      maxX = Math.max(maxX, n.x);
      minY = Math.min(minY, n.y);
      maxY = Math.max(maxY, n.y);
    });

    const treeWidth = maxX - minX + NODE_WIDTH;
    const treeHeight = maxY - minY + NODE_HEIGHT;

    // Initial transform to center and flip (root at bottom)
    const initialX = innerWidth / 2 - (minX + maxX) / 2;
    const initialY = innerHeight - NODE_HEIGHT / 2;
    const initialScale = Math.min(1, innerWidth / treeWidth, innerHeight / (treeHeight + 100));

    svg.call(
      zoom.transform,
      d3.zoomIdentity
        .translate(margin.left + initialX, margin.top + initialY)
        .scale(initialScale)
    );

    // Draw links (connectors)
    const linkGenerator = d3
      .linkVertical<d3.HierarchyPointLink<TreeNodeDatum>, d3.HierarchyPointNode<TreeNodeDatum>>()
      .x((d) => d.x)
      .y((d) => -d.y); // Flip Y for bottom-to-top

    g.selectAll(".link")
      .data(links)
      .enter()
      .append("path")
      .attr("class", "link")
      .attr("d", linkGenerator)
      .attr("fill", "none")
      .attr("stroke", "#94a3b8")
      .attr("stroke-width", 2);

    // Draw nodes
    const nodeGroups = g
      .selectAll(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", (d) => `translate(${d.x},${-d.y})`); // Flip Y

    // Node background (card)
    nodeGroups
      .append("rect")
      .attr("x", -NODE_WIDTH / 2)
      .attr("y", -NODE_HEIGHT / 2)
      .attr("width", NODE_WIDTH)
      .attr("height", NODE_HEIGHT)
      .attr("rx", 8)
      .attr("ry", 8)
      .attr("fill", (d) => (d.data.sex === "M" ? "#dbeafe" : d.data.sex === "F" ? "#fce7f3" : "#f1f5f9"))
      .attr("stroke", (d) =>
        d.data.sex === "M" ? "#3b82f6" : d.data.sex === "F" ? "#ec4899" : "#94a3b8"
      )
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("click", (_, d) => handleNodeClick(d.data.id));

    // Highlight root node
    nodeGroups
      .filter((d) => d.depth === 0)
      .select("rect")
      .attr("stroke-width", 3)
      .attr("stroke", "#7c3aed");

    // Gender indicator circle
    nodeGroups
      .append("circle")
      .attr("cx", -NODE_WIDTH / 2 + 16)
      .attr("cy", 0)
      .attr("r", 12)
      .attr("fill", (d) =>
        d.data.sex === "M" ? "#3b82f6" : d.data.sex === "F" ? "#ec4899" : "#94a3b8"
      );

    // Initial letter in circle
    nodeGroups
      .append("text")
      .attr("x", -NODE_WIDTH / 2 + 16)
      .attr("y", 4)
      .attr("text-anchor", "middle")
      .attr("fill", "white")
      .attr("font-size", "12px")
      .attr("font-weight", "bold")
      .text((d) => d.data.name.charAt(0).toUpperCase());

    // Name text
    nodeGroups
      .append("text")
      .attr("x", -NODE_WIDTH / 2 + 35)
      .attr("y", -8)
      .attr("text-anchor", "start")
      .attr("fill", "#1e293b")
      .attr("font-size", "13px")
      .attr("font-weight", "600")
      .each(function (d) {
        const text = d3.select(this);
        const name = d.data.name;
        // Truncate if too long
        const maxLength = 16;
        text.text(name.length > maxLength ? name.substring(0, maxLength) + "…" : name);
      })
      .style("cursor", "pointer")
      .on("click", (_, d) => handleNodeClick(d.data.id));

    // Dates text
    nodeGroups
      .append("text")
      .attr("x", -NODE_WIDTH / 2 + 35)
      .attr("y", 10)
      .attr("text-anchor", "start")
      .attr("fill", "#64748b")
      .attr("font-size", "11px")
      .text((d) => {
        const birth = d.data.birthDate;
        const death = d.data.deathDate;
        if (!birth && !death) return "";
        if (birth && death) return `${birth} – ${death}`;
        if (birth) return `b. ${birth}`;
        return `d. ${death}`;
      });

    // Expand/collapse buttons for nodes with parents
    const hasParents = (d: D3TreeNode): boolean =>
      d.data.father !== undefined || d.data.mother !== undefined || d.data._collapsed === true;

    nodeGroups
      .filter((d) => hasParents(d))
      .append("circle")
      .attr("cx", 0)
      .attr("cy", -NODE_HEIGHT / 2 - 12)
      .attr("r", 10)
      .attr("fill", (d) => (d.data._collapsed ? "#22c55e" : "#94a3b8"))
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        toggleCollapse(d.data.id);
      });

    // Plus/minus icon in button
    nodeGroups
      .filter((d) => hasParents(d))
      .append("text")
      .attr("x", 0)
      .attr("y", -NODE_HEIGHT / 2 - 8)
      .attr("text-anchor", "middle")
      .attr("fill", "white")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .style("pointer-events", "none")
      .text((d) => (d.data._collapsed ? "+" : "−"));

    // Generation labels
    const generations = new Map<number, number>();
    nodes.forEach((n) => {
      const y = -n.y;
      if (!generations.has(n.depth)) {
        generations.set(n.depth, y);
      }
    });

    generations.forEach((y, depth) => {
      if (GENERATION_LABELS[depth]) {
        g.append("text")
          .attr("x", minX - NODE_WIDTH / 2 - 20)
          .attr("y", y)
          .attr("text-anchor", "end")
          .attr("fill", "#64748b")
          .attr("font-size", "12px")
          .attr("font-style", "italic")
          .text(GENERATION_LABELS[depth]);
      }
    });
  }, [hierarchyData, dimensions, collapsedNodes, convertToD3Hierarchy, handleNodeClick, toggleCollapse]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        Failed to load family tree. Please try again.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-vault-200 p-4">
        <h3 className="font-serif text-lg font-semibold text-vault-900">
          Family Tree
        </h3>
        <div className="flex items-center gap-4 text-sm text-vault-500">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-blue-500"></div>
            <span>Male</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-pink-500"></div>
            <span>Female</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded-full bg-green-500"></div>
            <span>Click to expand</span>
          </div>
        </div>
      </div>

      {/* Tree container */}
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={{ height: "600px" }}
      >
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          className="bg-slate-50"
        />
      </div>

      {/* Instructions */}
      <div className="border-t border-vault-200 px-4 py-2 text-xs text-vault-500">
        Scroll to zoom • Drag to pan • Click a person to view details • Click green button to
        expand/collapse
      </div>
    </div>
  );
}
